import React, { useCallback } from 'react';
import { Platform } from 'react-native';
import { AutoSizeTextarea } from '../../../../apps/web/src/administration/components/worksheet/dynamic-grid/auto-size-textarea';
import { createUseStyles } from 'react-jss';
import { FormElementGridCellAnswer } from '@packages/models/api';
import { DynamicGridCell } from '../../../../apps/web/src/administration/components/worksheet/dynamic-grid/dynamic-grid-cell';
import { DynamicGridHeaderCell } from '../../../../apps/web/src/administration/components/worksheet/dynamic-grid/dynamic-grid-header-cell';
import { DynamicGridRow } from '../../../../apps/web/src/administration/components/worksheet/dynamic-grid/dynamic-grid-row';
import { DynamicGridRowType } from '../../../../apps/web/src/administration/components/worksheet/dynamic-grid/dynamic-grid-builder';
import { Typography } from '@packages/ui/shared';
import colors from '@packages/core/styles/colors';

interface DynamicGridProps {
    cornerHeader: string;
    onChange: (worksheetElementGridId: string, value: string) => void;
    onBlur: (worksheetElementGridId: string) => void;
    readOnly?: boolean;
    rows: DynamicGridRowType[];
    answers: FormElementGridCellAnswer[];
    isCellInvalid: (worksheetElementGridId: string) => boolean;
}

const useStyles = createUseStyles({
    container: {
        flex: 1,
        width: '100%',
        overflow: 'auto',
        position: 'relative',
    },
    table: {
        width: '100%',
        height: '100%',
        tableLayout: 'auto',
        borderCollapse: 'collapse',
        borderSpacing: 0,
    },
    cell: {
        border: `1px solid ${colors.grayThree}`,
    },
    cellInvalid: {
        outline: `1px solid ${colors.redOne}`,
        outlineOffset: -1,
        backgroundColor: colors.redFive,
    },
});

export function DynamicGrid({
    cornerHeader = '',
    onChange,
    onBlur,
    readOnly = false,
    rows,
    answers = [],
    isCellInvalid,
}: DynamicGridProps) {
    const classes = useStyles();
    const focusTextarea = useCallback((e: React.MouseEvent<HTMLTableCellElement>) => {
        const textarea = e.currentTarget.querySelector('textarea');
        if (textarea && !textarea.disabled) {
            textarea.focus();
        }
    }, []);

    if (Platform.OS !== 'web') {
        return null;
    }

    return (
        <div className={`${classes.container}`}>
            <table className={classes.table}>
                <thead>
                    <DynamicGridRow>
                        <DynamicGridHeaderCell className={classes.cell}>
                            <Typography
                                color={readOnly ? 'grayFive' : 'textDarkPrimary'}
                                numberOfLines={1}
                                style={{ fontWeight: 500 }}
                            >
                                {cornerHeader}
                            </Typography>
                        </DynamicGridHeaderCell>
                        {rows[0].columns.map((column, columnIndex) => (
                            <DynamicGridHeaderCell
                                key={`col-header-${column.columnOrder}-${columnIndex}`}
                                className={classes.cell}
                            >
                                <Typography
                                    style={{ fontWeight: 500 }}
                                    numberOfLines={1}
                                    color={readOnly ? 'grayFive' : 'textDarkPrimary'}
                                >
                                    {column.columnLabel}
                                    {column.isRequired && <Typography color="redOne"> *</Typography>}
                                </Typography>
                            </DynamicGridHeaderCell>
                        ))}
                    </DynamicGridRow>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <DynamicGridRow key={`row-${row.rowOrder}`}>
                            <DynamicGridHeaderCell className={classes.cell}>
                                <Typography
                                    style={{ fontWeight: 500 }}
                                    numberOfLines={1}
                                    color={readOnly ? 'grayFive' : 'textDarkPrimary'}
                                >
                                    {row.rowLabel}
                                </Typography>
                            </DynamicGridHeaderCell>
                            {row.columns.map((column) => {
                                const isInvalid = isCellInvalid(column.worksheetElementGridId);
                                const value =
                                    answers.find(
                                        (cellAnswer) =>
                                            cellAnswer.worksheetElementGridId === column.worksheetElementGridId
                                    )?.value ?? '';

                                return (
                                    <DynamicGridCell
                                        key={`cell-${row.rowOrder}-${column.columnOrder}-${column.worksheetElementGridId}`}
                                        onClick={focusTextarea}
                                        className={classes.cell}
                                        invalid={isInvalid}
                                        readOnly={readOnly}
                                    >
                                        <AutoSizeTextarea
                                            id={`input-${column.worksheetElementGridId}`}
                                            value={value}
                                            onChange={(value) => {
                                                onChange(column.worksheetElementGridId, value);
                                            }}
                                            onBlur={() => {
                                                onBlur(column.worksheetElementGridId);
                                            }}
                                            required={column.isRequired}
                                            disabled={readOnly}
                                            placeholder={isInvalid ? 'Required' : ''}
                                        />
                                    </DynamicGridCell>
                                );
                            })}
                        </DynamicGridRow>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
